import type React from 'react';
import { useEffect } from 'react';
import { debounce } from 'lodash';
import useResizeObserver from 'use-resize-observer';
import { useMemoizedFn, useSafeState } from 'ahooks';

type UseParentAutoHeightProps = {
  parentContainerRef: React.RefObject<HTMLElement>; // 父容器的ref
  containerRef: React.RefObject<HTMLElement>; // 当前容器的ref
  initialHeight?: number; // 初始高度
  minHeight?: number; // 最小高度
  placeholderHeight?: number; // 占位高度
  dependency?: any; // 高度设置依赖项
};

const useParentAutoHeight = ({
  parentContainerRef,
  containerRef,
  initialHeight = 0,
  minHeight = 0,
  placeholderHeight = 0,
  dependency
}: UseParentAutoHeightProps) => {
  const [height, setHeight] = useSafeState<number>(initialHeight);

  const _setHeight = useMemoizedFn(() => {
    const parentInnerHeight = parentContainerRef.current?.clientHeight as number;
    const topGap =
      (containerRef.current?.getBoundingClientRect().top as number) -
      (parentContainerRef.current?.getBoundingClientRect().top as number);
    const _height = parentInnerHeight - topGap - placeholderHeight;
    setHeight(_height < Number(minHeight) ? minHeight : _height);
  });

  //自适应高度
  const setHeightWhenResize = useMemoizedFn(debounce(_setHeight, 200));

  useResizeObserver({
    ref: containerRef,
    onResize: () => {
      setHeightWhenResize();
    }
  });

  //监听设置高度
  useEffect(() => {
    const timer = setTimeout(_setHeight, 100);
    return () => {
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dependency]);

  return { height };
};

export default useParentAutoHeight;
